import { GenericTableCell } from "gen/model/genericTableCell";
import { GenericTableColumnCellValueType } from "gen/model/genericTableColumnCellValueType";
import { GenericTableColumnSetFilterValue } from "gen/model/genericTableColumnSetFilterValue";
import { GenericTableData } from "gen/model/genericTableData";
import { GenericTableRow } from "gen/model/genericTableRow";
import log from "loglevel";
import { useEffect, useMemo } from "react";
import {
  DataTableColumnInferredData,
  DataTableContext,
} from "shared/components/table/data/DataTableContext";
import { DataTableProps } from "shared/components/table/data/DataTableProps";
import { DataTableGetFormFn } from "shared/components/table/data/internal/DataTableForm";
import {
  iterateGenericTableColumns,
  iterateGenericTableRows,
  valueForCell,
} from "shared/components/table/data/internal/DataTableInternalUtils";
import { useMaybernTableUIContext } from "shared/components/table/maybern/MaybernTableUIContext";
import { tryGetAgGridApi } from "shared/components/table/maybern/MaybernTableUtils";

export const useBuildDataTableContext = (
  {
    data: tableData,
    disablePinnedHeaderColumns = false,
    shouldUnregisterFormFields = true,
    aggFunc,
    headerOrCellClass,
    headerClass,
    cellClassRules,
    rowClassRules,
    getActionsForColumn,
    getActionsForCell,
    onButtonClick,
    renderRowActions,
    getColumnDef,
    getLinkInfoForRow,
    getRowSpanKeyForCell,
    stylesForCell,
    renderCellValue,
    getLinkInfoForCell,
    getLinkInfoForCellBadge,
    lockColumns,
    showTotalRow,
    valueGetter,
    serverSideDatasource,
  }: DataTableProps,
  getForm: DataTableGetFormFn,
) => {
  const { agGrid } = useMaybernTableUIContext();

  const context = useMemo<DataTableContext>(
    () => ({
      isEmpty:
        !tableData.columns.length ||
        (!tableData.rows.length && !serverSideDatasource),
      rowDataPaths: makeRowDataPaths(tableData),
      columnInferredData: makeColumnInferredData(tableData),
      isClientRowAggregation: tableData.client_row_aggregation ?? false,
      hasExpandableRows: tableData.rows.some((row) => row.child_rows?.length),
      disablePinnedHeaderColumns,
      shouldUnregisterFormFields,
      aggFunc,
      getForm,
      headerOrCellClass,
      headerClass,
      cellClassRules,
      rowClassRules,
      getActionsForCell,
      getActionsForColumn,
      onButtonClick,
      renderRowActions,
      getColumnDef,
      getLinkInfoForRow,
      getRowSpanKeyForCell,
      stylesForCell,
      renderCellValue,
      getLinkInfoForCell,
      getLinkInfoForCellBadge,
      lockColumns,
      showTotalRow,
      valueGetter,
    }),
    [
      tableData,
      serverSideDatasource,
      disablePinnedHeaderColumns,
      shouldUnregisterFormFields,
      aggFunc,
      getForm,
      headerOrCellClass,
      headerClass,
      cellClassRules,
      rowClassRules,
      getActionsForCell,
      getActionsForColumn,
      onButtonClick,
      renderRowActions,
      getColumnDef,
      getLinkInfoForRow,
      getRowSpanKeyForCell,
      stylesForCell,
      renderCellValue,
      getLinkInfoForCell,
      getLinkInfoForCellBadge,
      lockColumns,
      showTotalRow,
      valueGetter,
    ],
  );

  // Refresh the grid when the context changes.
  // This does not happen automatically, see sample code at:
  // https://www.ag-grid.com/javascript-data-grid/context/
  useEffect(() => {
    void context;
    const api = tryGetAgGridApi(agGrid);
    if (api) {
      api.refreshCells();
      api.refreshHeader();
    }
  }, [agGrid, context]);

  return context;
};

const makeRowDataPaths = (tableData: GenericTableData) => {
  const map = new Map<string, string[]>();
  const visit = (rows: GenericTableRow[], parentPath: string[] = []) => {
    rows.forEach((row) => {
      const rowId = row.id;
      if (map.has(rowId)) {
        log.warn(`Duplicate row id: ${rowId}`);
      }
      const path = [...parentPath, rowId];
      map.set(rowId, path);
      if (row.child_rows) {
        visit(row.child_rows, path);
      }
    });
  };
  visit(tableData.rows);
  return map;
};

const makeColumnInferredData = (tableData: GenericTableData) => {
  const columnValueTypes = new Map<string, GenericTableColumnCellValueType>();
  const columnSetFilterValues = new Map<
    string,
    Set<GenericTableColumnSetFilterValue>
  >();
  const columnCurrencies = new Map<string, string>();
  const columnHasFormField = new Map<string, boolean>();

  const addFilterValueToSet = (
    columnId: string,
    filterValue: GenericTableColumnSetFilterValue,
  ) => {
    if (!columnSetFilterValues.has(columnId)) {
      columnSetFilterValues.set(columnId, new Set());
    }
    columnSetFilterValues.get(columnId)!.add(filterValue);
  };

  for (const row of iterateGenericTableRows(tableData.rows)) {
    for (const column of iterateGenericTableColumns(tableData.columns)) {
      const cell = row.cells[column.id] as GenericTableCell | undefined;
      if (!cell) {
        continue;
      }
      if (cell.value?.type) {
        columnValueTypes.set(column.id, cell.value.type);
      }
      if (cell.value?.type === "money" && cell.value.currency) {
        columnCurrencies.set(column.id, cell.value.currency);
      }
      if (cell.form_field) {
        columnHasFormField.set(column.id, true);
      }
      if (column.filter_type === "set") {
        if (cell.filter_values) {
          for (const filterValue of cell.filter_values) {
            addFilterValueToSet(column.id, {
              id: filterValue,
              value: filterValue,
            });
          }
        } else {
          const filterValue = valueForCell(cell)?.toString();
          if (filterValue) {
            addFilterValueToSet(column.id, {
              id: filterValue,
              value: filterValue,
            });
          }
        }
      } else if (cell.badge?.text) {
        const value = cell.badge.text;
        addFilterValueToSet(column.id, { id: value, value });
      }
    }
  }

  const inferredDatas = new Map<string, DataTableColumnInferredData>();

  for (const column of iterateGenericTableColumns(tableData.columns)) {
    const setFilterValues = columnSetFilterValues.get(column.id);
    const inferredData: DataTableColumnInferredData = {
      valueType: columnValueTypes.get(column.id),
      currency: columnCurrencies.get(column.id),
      hasFormField: columnHasFormField.get(column.id),
      setFilterValues: setFilterValues
        ? Array.from(setFilterValues).sort((a, b) =>
            a.value.localeCompare(b.value),
          )
        : undefined,
    };
    inferredDatas.set(column.id, inferredData);
  }

  return inferredDatas;
};
